import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { syncPushSubscription, unsubscribeFromPush } from "../utils/pushNotifications";
import { readStoredRoles } from "./roleStorage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MobileAuthContext = createContext(null);

export const useMobileAuth = () => useContext(MobileAuthContext);

export const MobileAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("mobile_token"));
  const [availableRoles, setAvailableRoles] = useState(readStoredRoles);
  const [loading, setLoading] = useState(true);

  const getHeaders = useCallback(() => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [token]);

  useEffect(() => {
    if (token) {
      axios
        .get(`${API}/mobile/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => {
          setUser(res.data);
          if (Array.isArray(res.data?.available_roles)) {
            localStorage.setItem("mobile_available_roles", JSON.stringify(res.data.available_roles));
            setAvailableRoles(res.data.available_roles);
          }
        })
        .catch(() => {
          localStorage.removeItem("mobile_token");
          localStorage.removeItem("mobile_available_roles");
          setToken(null);
          setUser(null);
          setAvailableRoles([]);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = (newToken, userData, roles = []) => {
    localStorage.setItem("mobile_token", newToken);
    localStorage.setItem("mobile_available_roles", JSON.stringify(roles));
    setToken(newToken);
    setUser(userData);
    setAvailableRoles(roles);
  };

  useEffect(() => {
    const refreshProfile = async () => {
      if (!token || localStorage.getItem("mobile_token") !== token) return;
      try {
        const res = await axios.get(`${API}/mobile/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        if (localStorage.getItem("mobile_token") === token) setUser(res.data);
      } catch { /* Keep the active session during a temporary network failure. */ }
    };
    window.addEventListener("focus", refreshProfile);
    window.addEventListener("mobile-profile-updated", refreshProfile);
    return () => {
      window.removeEventListener("focus", refreshProfile);
      window.removeEventListener("mobile-profile-updated", refreshProfile);
    };
  }, [token]);

  const logout = async () => {
    await unsubscribeFromPush(token);
    localStorage.removeItem("mobile_token");
    localStorage.removeItem("mobile_available_roles");
    setToken(null);
    setUser(null);
    setAvailableRoles([]);
  };

  const switchRole = async (targetRole) => {
    if (!token || !targetRole) return null;
    const r = await axios.post(
      `${API}/mobile/auth/switch-role`,
      { role: targetRole },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (r.data?.noop) return r.data;
    // Retain the old token until this installation's old subscription is revoked.
    await unsubscribeFromPush(token);
    const newRoles = r.data?.available_roles || availableRoles;
    // Pure state swap — NO page reload. React unmounts the previous
    // role-specific dashboard and mounts the new one automatically because
    // MobileApp reads `user.role` and re-renders. Avoiding the reload kills
    // the splash + login-flash that users perceived as "rebooting".
    login(r.data.token, r.data.user, newRoles);
    return r.data;
  };

  useEffect(() => {
    if (!token || !user?.id) return;
    const sync = () => syncPushSubscription(token).catch(() => window.dispatchEvent(new Event("push-sync-error")));
    sync();
    window.addEventListener("focus", sync);
    return () => window.removeEventListener("focus", sync);
  }, [token, user?.id]);

  return (
    <MobileAuthContext.Provider
      value={{ user, token, loading, login, logout, getHeaders, availableRoles, switchRole }}
    >
      {children}
    </MobileAuthContext.Provider>
  );
};
